/* eslint-disable */
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { NativeBiometric } from "@capgo/capacitor-native-biometric";
import { SecureStorage, KeychainAccess } from "@aparajita/capacitor-secure-storage";

/*
  PresentPlus biometric rules:
  - One enabled key only.
  - Parent credentials are stored only in native secure storage after password
    reauthentication and a successful Face ID / Touch ID check.
  - The login page can use Face ID to unlock those credentials and sign in.
  - Firestore only stores biometricEnabled; sensitive login secrets stay local.
*/

export const BIOMETRIC_ENABLED_KEY = "pp_parent_biometric_enabled_v5";
export const BIOMETRIC_EMAIL_KEY = "pp_parent_biometric_email_v5";
export const BIOMETRIC_CREDENTIALS_KEY = "pp_parent_biometric_credentials_v5";
export const REMEMBERED_EMAIL_KEY = "pp_parent_remembered_email";
export const BIOMETRIC_UNLOCK_SESSION_KEY = "pp_parent_biometric_unlocked_at";

const LEGACY_PREFERENCES_KEYS = [
  "pp_credentials_v2",
  "pp_credentials",
  "pp_parent_biometric_creds_v1",
  "pp_parent_biometric_enabled_v1",
  "pp_parent_biometric_enabled_v2",
  "pp_parent_biometric_enabled_v3",
  "pp_parent_biometric_enabled_v4",
  "pp_parent_biometric_email_v1",
  "pp_parent_biometric_email_v2",
  "pp_parent_biometric_email_v3",
  "pp_parent_biometric_email_v4",
  "pp_parent_biometric_user_v2",
];

const SERVER = "presentplus-parent";
const SECURE_PREFIX = "presentplus_parent_";
let securePrefixReady = false;

let cachedCredentials = null;

function isNative() {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

function cleanEmail(value) {
  return String(value || "").trim().toLowerCase();
}

export function sleep(ms = 250) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function prepareSecureStorage() {
  if (securePrefixReady) return;

  try {
    await SecureStorage.setKeyPrefix(SECURE_PREFIX);
  } catch (err) {
    console.warn("Secure storage prefix setup failed:", err);
  }

  securePrefixReady = true;
}

async function secureSet(key, value) {
  await prepareSecureStorage();

  try {
    await SecureStorage.set(
      key,
      value,
      true,
      false,
      KeychainAccess?.whenUnlockedThisDeviceOnly
    );
  } catch (err) {
    console.warn("SecureStorage.set with access option failed, retrying:", err);
    await SecureStorage.set(key, value);
  }
}

async function secureGet(key) {
  await prepareSecureStorage();
  return SecureStorage.get(key);
}

async function secureRemove(key) {
  await prepareSecureStorage();

  try {
    await SecureStorage.remove(key, false);
  } catch {
    try {
      await SecureStorage.remove(key);
    } catch {}
  }
}

export async function clearLegacyBiometricState() {
  await Promise.all(
    LEGACY_PREFERENCES_KEYS.map((key) =>
      Preferences.remove({ key }).catch(() => {})
    )
  );

  if (isNative()) {
    try {
      await NativeBiometric.deleteCredentials({ server: SERVER });
    } catch {
      // Ignore old NativeBiometric keychain records; they are no longer used.
    }
  }

  return true;
}

export async function clearAllBiometricState() {
  cachedCredentials = null;

  await Promise.all([
    Preferences.remove({ key: BIOMETRIC_ENABLED_KEY }).catch(() => {}),
    Preferences.remove({ key: BIOMETRIC_EMAIL_KEY }).catch(() => {}),
    ...LEGACY_PREFERENCES_KEYS.map((key) =>
      Preferences.remove({ key }).catch(() => {})
    ),
  ]);

  await secureRemove(BIOMETRIC_CREDENTIALS_KEY);

  if (isNative()) {
    try {
      await NativeBiometric.deleteCredentials({ server: SERVER });
    } catch {}
  }

  try {
    sessionStorage.removeItem(BIOMETRIC_UNLOCK_SESSION_KEY);
  } catch {}

  return true;
}

export async function getBiometricAvailability() {
  if (!isNative()) {
    return {
      isAvailable: false,
      label: "Quick unlock",
      raw: null,
    };
  }

  try {
    const raw = await NativeBiometric.isAvailable();

    const isAvailable = Boolean(
      raw?.isAvailable ||
        raw?.strongBiometryIsAvailable ||
        raw?.biometryType ||
        raw?.biometricType
    );

    const type = String(
      raw?.biometryType ||
        raw?.biometricType ||
        raw?.type ||
        ""
    ).toLowerCase();

    let label = "Quick unlock";
    if (type.includes("face") || raw?.biometryType === 2) label = "Face ID";
    if (type.includes("touch") || type.includes("finger") || raw?.biometryType === 1) {
      label = "Touch ID";
    }

    return {
      isAvailable,
      label,
      raw,
    };
  } catch (err) {
    console.warn("Biometric availability check failed:", err);
    return {
      isAvailable: false,
      label: "Quick unlock",
      raw: null,
    };
  }
}

export async function verifyBiometric({
  reason = "Unlock PresentPlus Parent",
  title = "PresentPlus",
  subtitle = "Confirm it is you",
  description = "Use your device biometric method to continue.",
  negativeButtonText = "Cancel",
} = {}) {
  if (!isNative()) {
    throw new Error("Biometric unlock is only available inside the installed app.");
  }

  await sleep(200);

  const availability = await getBiometricAvailability();

  if (!availability.isAvailable) {
    throw new Error(
      "Biometric unlock is not available on this device. Please make sure Face ID, Touch ID, or device passcode is enabled."
    );
  }

  await NativeBiometric.verifyIdentity({
    reason,
    title,
    subtitle,
    description,
    negativeButtonText,
  });

  return true;
}

export async function setBiometricEnabled(enabled, email = "") {
  const next = Boolean(enabled);

  if (!next) {
    await clearAllBiometricState();
    return { success: true, enabled: false };
  }

  const clean = cleanEmail(email);

  await Preferences.set({ key: BIOMETRIC_ENABLED_KEY, value: "true" });

  if (clean) {
    await Preferences.set({ key: BIOMETRIC_EMAIL_KEY, value: clean });
    try {
      localStorage.setItem(REMEMBERED_EMAIL_KEY, clean);
    } catch {}
  }

  return { success: true, enabled: true, email: clean };
}

export async function isBiometricEnabled() {
  try {
    const { value } = await Preferences.get({ key: BIOMETRIC_ENABLED_KEY });
    return value === "true";
  } catch {
    return false;
  }
}

export async function getBiometricEmail() {
  try {
    const { value } = await Preferences.get({ key: BIOMETRIC_EMAIL_KEY });
    return cleanEmail(value);
  } catch {
    return "";
  }
}

export async function storeCredentials(username, password = "") {
  const email = cleanEmail(username);
  const secret = String(password || "");

  if (!email || !secret) {
    throw new Error("A valid email and password are required to enable Face ID login.");
  }

  if (!isNative()) {
    throw new Error("Face ID login can only be enabled inside the installed app.");
  }

  const payload = {
    username: email,
    password: secret,
    storedAt: Date.now(),
  };

  await secureSet(BIOMETRIC_CREDENTIALS_KEY, payload);
  cachedCredentials = { username: email, password: secret };

  await setBiometricEnabled(true, email);

  return { success: true, username: email };
}

export async function getStoredCredentials() {
  try {
    const enabled = await isBiometricEnabled();
    if (!enabled) {
      cachedCredentials = null;
      return null;
    }

    if (cachedCredentials?.username && cachedCredentials?.password) {
      return cachedCredentials;
    }

    const saved = await secureGet(BIOMETRIC_CREDENTIALS_KEY);

    const username = cleanEmail(saved?.username || saved?.email || "");
    const password = String(saved?.password || "");

    if (!username || !password) return null;

    cachedCredentials = { username, password };
    return cachedCredentials;
  } catch (err) {
    console.warn("Failed reading biometric credentials:", err);
    return null;
  }
}

export async function clearCredentials() {
  await clearAllBiometricState();
  return true;
}

export async function clearBiometricPreference() {
  return clearCredentials();
}

export async function saveBiometricLoginCredentials(email, password) {
  return storeCredentials(email, password);
}

export async function getBiometricLoginCredentials() {
  const credentials = await getStoredCredentials();

  if (!credentials?.username || !credentials?.password) {
    return {
      success: false,
      email: await getBiometricEmail(),
      password: "",
    };
  }

  return {
    success: true,
    email: credentials.username,
    username: credentials.username,
    password: credentials.password,
  };
}

export async function clearBiometricLoginCredentials() {
  await clearCredentials();
  return { success: true };
}
